import { confirmationMatches } from '@pb/shared';
import { useState, type ReactNode } from 'react';
import { Dialog } from './Dialog';
import { ErrorAlert } from './States';

interface TypedConfirmProps {
  title: string;
  name: string;
  what: string;
  actionLabel: string;
  busyLabel?: string;
  danger?: boolean;
  children?: ReactNode;
  onConfirm: (confirm: string) => Promise<void>;
  onClose: () => void;
}

export function TypedConfirmDialog({
  title,
  name,
  what,
  actionLabel,
  busyLabel = 'Working…',
  danger = true,
  children,
  onConfirm,
  onClose,
}: TypedConfirmProps) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const matches = confirmationMatches(value, name);

  const submit = async () => {
    if (!matches || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(value);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="typed-confirm-form"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            disabled={!matches || busy}
          >
            {busy ? busyLabel : actionLabel}
          </button>
        </>
      }
    >
      {children}
      <form
        id="typed-confirm-form"
        className="field"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label htmlFor="typed-confirm-input">
          Type the {what} <strong>{name}</strong> to confirm.
        </label>
        <input
          id="typed-confirm-input"
          className="input"
          value={value}
          autoComplete="off"
          data-autofocus
          onChange={(event) => setValue(event.target.value)}
        />
      </form>
      <ErrorAlert error={error} />
    </Dialog>
  );
}
